import React, { useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, AlertTriangle, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel, scrollToId } from "./Reveal";
import { PRICING } from "../../data";
import { useLang } from "../../i18n";

const perPerson = (n) => (n >= 11 ? 175 : n >= 6 ? 200 : 225);
const OSHA_FINE = 161323;

export const Pricing = () => {
  const { t } = useLang();
  const p = t.pricing;
  const [count, setCount] = useState(8);
  const [codes, setCodes] = useState(1);
  const rate = perPerson(count);
  const total = rate * count + Math.max(0, codes - 1) * 65 * count;
  const clamp = (v) => Math.min(200, Math.max(1, v));

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 sm:py-32 bg-[#0a1430] border-y border-white/10"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionLabel num="[ 04 ]">{p.label}</SectionLabel>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-14">
            <h2 className="font-head font-extrabold uppercase tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.9] max-w-2xl">
              {p.title1}
              <span className="block text-gold-grad">{p.title2}</span>
            </h2>
            <p className="text-slate-400 max-w-sm">{p.intro}</p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-5 mb-14">
          {PRICING.map((tier, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div
                data-testid={`price-tier-${i}`}
                className={`relative rounded-2xl p-8 h-full flex flex-col ${
                  tier.popular ? "bg-amber-400 text-[#060c1f]" : "card-hairline text-white"
                }`}
              >
                {tier.popular && (
                  <span className="absolute top-5 right-5 font-mono-x text-[10px] uppercase tracking-[0.2em] bg-[#060c1f] text-amber-400 px-3 py-1 rounded-full">
                    {p.popular}
                  </span>
                )}
                <span className={`font-mono-x text-xs uppercase tracking-[0.2em] ${tier.popular ? "text-[#060c1f]/70" : "text-amber-400"}`}>
                  {p.tierNames[i]}
                </span>
                <div className="mt-5 flex items-end gap-1">
                  <span className="font-head font-extrabold text-6xl leading-none">${tier.price}</span>
                  <span className={`mb-1.5 text-sm ${tier.popular ? "text-[#060c1f]/60" : "text-slate-500"}`}>
                    {p.perPerson}
                  </span>
                </div>
                <p className={`mt-3 text-sm ${tier.popular ? "text-[#060c1f]/70" : "text-slate-400"}`}>
                  {p.tierNotes[i]}
                </p>
                <div className={`mt-5 pt-5 text-sm border-t ${tier.popular ? "border-[#060c1f]/15" : "border-white/10"}`}>
                  {p.addCode}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="grid lg:grid-cols-5 gap-5">
          <Reveal className="lg:col-span-3">
            <div className="rounded-2xl card-hairline p-7 sm:p-9 h-full" data-testid="price-calculator">
              <h3 className="font-head font-bold uppercase tracking-wide text-xl text-white mb-7">{p.calcTitle}</h3>

              <Stepper label={p.operators} value={count} onDec={() => setCount((c) => clamp(c - 1))} onInc={() => setCount((c) => clamp(c + 1))} testid="calc-operators" />
              <div className="h-5" />
              <Stepper label={p.codes} value={codes} onDec={() => setCodes((c) => Math.max(1, c - 1))} onInc={() => setCodes((c) => Math.min(7, c + 1))} testid="calc-codes" />

              <div className="mt-8 flex items-end justify-between border-t border-white/10 pt-6">
                <div>
                  <div className="font-mono-x text-xs text-slate-500 uppercase tracking-widest">
                    {p.rate} ${rate}{p.perPersonShort} · {count}
                  </div>
                  <div className="font-mono-x text-xs text-slate-500">{p.estTotal}</div>
                </div>
                <motion.div
                  key={total}
                  initial={{ opacity: 0.4, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="font-head font-extrabold text-5xl text-gold-grad"
                  data-testid="calc-total"
                >
                  ${total.toLocaleString()}
                </motion.div>
              </div>
              <button
                onClick={() => scrollToId("quote")}
                data-testid="calc-quote-btn"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-[#060c1f] font-head font-bold uppercase tracking-wide py-3.5 rounded-md transition-colors"
              >
                {p.lockIn} <ArrowRight size={17} />
              </button>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-2" delay={0.08}>
            <div className="rounded-2xl p-7 sm:p-9 h-full border border-red-500/20 bg-red-500/[0.04]" data-testid="osha-risk">
              <div className="flex items-center gap-2 text-red-400 mb-4">
                <AlertTriangle size={18} />
                <span className="font-mono-x text-xs uppercase tracking-[0.2em]">{p.riskLabel}</span>
              </div>
              <div className="font-head font-extrabold text-5xl text-white leading-none">${OSHA_FINE.toLocaleString()}</div>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{p.riskBody}</p>
              <div className="mt-6 flex items-center gap-3">
                <span className="font-head font-bold text-emerald-400 text-lg">${total.toLocaleString()}</span>
                <span className="text-slate-500 text-sm">{p.yourCost}</span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.05}>
          <div className="mt-5 grid sm:grid-cols-2 gap-5">
            <TrainerCard title={p.trainer1Title} price="$400" body={p.trainer1Body} />
            <TrainerCard title={p.trainer2Title} price="$600" body={p.trainer2Body} />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Stepper = ({ label, value, onDec, onInc, testid }) => (
  <div>
    <div className="font-mono-x text-xs text-slate-400 uppercase tracking-widest mb-3">{label}</div>
    <div className="flex items-center gap-4">
      <button onClick={onDec} data-testid={`${testid}-dec`} className="h-11 w-11 rounded-lg border border-white/15 hover:border-amber-400 hover:text-amber-400 text-white flex items-center justify-center transition-colors">
        <Minus size={18} />
      </button>
      <span className="font-head font-extrabold text-4xl text-white w-16 text-center" data-testid={`${testid}-value`}>
        {value}
      </span>
      <button onClick={onInc} data-testid={`${testid}-inc`} className="h-11 w-11 rounded-lg border border-white/15 hover:border-amber-400 hover:text-amber-400 text-white flex items-center justify-center transition-colors">
        <Plus size={18} />
      </button>
    </div>
  </div>
);

const TrainerCard = ({ title, price, body }) => (
  <div className="rounded-2xl card-hairline p-7 flex items-start justify-between gap-4">
    <div>
      <h4 className="font-head font-bold uppercase tracking-wide text-lg text-white leading-tight">{title}</h4>
      <p className="mt-2 text-sm text-slate-400">{body}</p>
    </div>
    <span className="font-head font-extrabold text-3xl text-gold-grad shrink-0">{price}</span>
  </div>
);
